import {
  Box,
  ClickAwayListener,
  Divider,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import Quill, { DeltaStatic, Sources } from "quill";
import { forwardRef, useEffect, useRef, useState } from "react";
import { v4 } from "uuid";
import QuillToolBar from "./quillToolbar";
import ReactQuill, { ReactQuillProps } from "react-quill";
import PropTypes from "prop-types";
import { extractStringFromHtml } from "utilz/helpers";

// Extend ReactQuillProps and add custom props
interface QuillEditorProps extends Omit<ReactQuillProps, "onChange"> {
  name?: string;
  onChange: (
    { field, value }: { field: string; value: string },
    meta?: {
      value: string;
      delta: DeltaStatic;
      source: Sources;
      editor: ReactQuill.UnprivilegedEditor;
    },
  ) => void;
  onTextChange?: () => void; // Add onTextChange prop
  isRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  showToolbar?: boolean;
  maxHeight?: string;
  minHeight?: string;
}

const Delta = Quill.import("delta");

const CustomQuillEditor = forwardRef(
  ({ onChange, onTextChange, ...props }: QuillEditorProps, customRef: any) => {
    const quillRef = useRef<ReactQuill | null>(null);

    // Handle text changes from Quill
    const onChangeHandler = (
      value: string,
      delta: DeltaStatic,
      source: Sources,
      editor: ReactQuill.UnprivilegedEditor,
    ) => {
      onChange(
        {
          field: props.name || "",
          value: extractStringFromHtml(value).length > 0 ? value : "",
        },
        {
          value,
          delta,
          source,
          editor,
        },
      );

      // Trigger onTextChange if provided
      if (onTextChange) {
        onTextChange();
      }
    };

    useEffect(() => {
      if (!quillRef.current || !onTextChange) return;

      const quillInstance = quillRef.current.getEditor();
      const handleTextChange = () => {
        onTextChange();
      };

      quillInstance.on("text-change", handleTextChange);

      return () => {
        quillInstance.off("text-change", handleTextChange);
      };
    }, [onTextChange]);

    // Sync editor content with value prop
    useEffect(() => {
      if (!quillRef.current || props.value === undefined) return;

      const editor: any = quillRef.current.getEditor();
      const currentContent = editor.root.innerHTML;

      if (props.value !== currentContent) {
        const selection = editor.getSelection();
        editor.root.innerHTML = props.value;
        if (selection) {
          const safePosition = Math.min(
            selection.index,
            editor.getLength() - 1,
          );
          editor.setSelection(safePosition, 0);
        }
      }
    }, [props.value]);

    return (
      <Box
        sx={{
          width: "100%",
          "& .ql-editor": {
            flex: 1,
            minHeight: props?.minHeight || "300px",
            maxHeight: props?.maxHeight || "300px",
            height: "auto",
            overflowY: "auto",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            border: props.error ? "1px solid #EF4C54" : "1px solid #EDEDED",
            padding: "16px",
            margin: props?.error ? "8px 16px 16px 16px" : "16px",
            backgroundColor: "#F6F6F5",
            borderRadius: "4px",
          },
          "& .ql-toolbar.ql-snow": {
            p: "0px !important",
            m: "0px !important",
            borderTop: "1px solid #E3E3E3",
            borderBottom: "1px solid #E3E3E3",
            borderLeft: "none",
            borderRight: "none",
          },
          "& .ql-snow.ql-toolbar button": {
            borderRadius: "6px",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "7px",
          },
          "& .ql-snow.ql-toolbar button.ql-active,.ql-snow .ql-toolbar button.ql-active,.ql-snow.ql-toolbar .ql-picker-label.ql-active,.ql-snow .ql-toolbar .ql-picker-label.ql-active,.ql-snow.ql-toolbar .ql-picker-item.ql-selected,.ql-snow .ql-toolbar .ql-picker-item.ql-selected":
            {
              backgroundColor: "#EDEDED",
              color: "black",
            },
          "& .ql-snow.ql-toolbar.ql-stroke": {
            color: "black",
          },
          "& .ql-container": {
            border: "none !important",
            height: "auto",
          },
          "& .ql-container.ql-snow": {
            backgroundColor: "#FFF",
            overflow: "scroll",
            height: "100%",
            display: "flex",
            border: "none",
          },
          "& .ql-editor > p": {
            color: "#767B85",
          },
          "& .ql-editor.ql-blank::before": {
            paddingLeft: "16px",
          },
        }}
      >
        <div
          id="quill-editor"
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          {props?.showToolbar && <QuillToolBar />}
          {props?.error && (
            <Typography
              pl="16px"
              mt="16px"
              variant="body-small"
              color={"error"}
            >
              {props?.errorMessage
                ? props?.errorMessage
                : "This is required field"}
            </Typography>
          )}

          <ReactQuill
            ref={(el) => {
              quillRef.current = el;
              if (customRef) {
                if (typeof customRef === "function") customRef(el);
                else customRef.current = el;
              }
            }}
            style={{
              height: "100%",
              overflow: "scroll",
              whiteSpace: "pre-line",
            }}
            theme="snow"
            value={props.value}
            onChange={onChangeHandler}
            placeholder={`${props.placeholder} ${props.isRequired ? "*" : ""}`}
            modules={{
              toolbar: props.showToolbar
                ? {
                    container: "#quill-toolbar",
                  }
                : false,
            }}
            readOnly={props.readOnly}
            onKeyDown={props.onKeyDown}
            onKeyUp={props.onKeyUp}
            {...props}
          />
        </div>
      </Box>
    );
  },
);

CustomQuillEditor.displayName = "CustomQuillEditor";

export default CustomQuillEditor;
